import type { ComponentProps, MutableRefObject, ReactNode } from 'react';
import React, { useDeferredValue } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  HeightProps,
  LeftProps,
  PositionProps,
  ResponsiveValue,
  TopProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  boxShadow,
  color,
  compose,
  display,
  flexDirection,
  height,
  left,
  position,
  right,
  backgroundColor as styledBackgroundColor,
  width as styledWidth,
  top,
  zIndex,
} from 'styled-system';

import { BACKDROP_Z_INDEX, MODAL_Z_INDEX } from '../constants/z-index';
import { useHiddenBodyOverflow } from '../hooks/scroll';
import { useLocalization } from '../providers/LocaleProvider';
import type { BackgroundColorProps } from '../theme/system-types';
import { themeWidth } from '../theme/system-utilities';
import LazyFocusLock from './LazyFocusLock';

interface DrawerProps extends WidthProps {
  id?: string;
  size?: ResponsiveValue<number>;
  isOpen?: boolean;
  isSideNav?: boolean;
  onBackdropClick?: Nullable<() => void>;
  children?: ReactNode;
  ariaLabelledby?: string;
  containerRef?: MutableRefObject<Nullable<HTMLElement>>;
  onKeyDown?: (event: KeyboardEvent) => void;
  backgroundColor?: string;
  isLeftOpener?: boolean;
  incrementZIndex?: boolean;
  transitionDisabled?: boolean;
}

export const DRAWER_WIDTH = ['100%', '448px', '496px', '600px'];

const DrawerContainer = styled.aside<any>`
  ${compose(
    position,
    top,
    right,
    themeWidth as any,
    height,
    styledBackgroundColor,
    boxShadow,
    zIndex,
    display,
    flexDirection,
    color
  )}
  transition-property: transform, width;
  transition-duration: 0.16s;
  transition-timing-function: ${props => (props.isOpen ? 'ease-out' : 'ease-in')};
  transform: ${props =>
    props.isOpen
      ? 'translate3d(0, 0, 0)'
      : props.isLeftOpener
        ? 'translate3d(-100%, 0, 0)'
        : 'translate3d(100%, 0, 0)'};
  ${props =>
    props.isLeftOpener &&
    css`
      left: 0;
      box-shadow: 0 0 4px 0 rgba(44, 54, 56, 0.3);
    `};
`;

const drawerContainerStyles = {
  zIndex: MODAL_Z_INDEX,
  position: 'fixed',
  top: 0,
  right: 0,
  themeWidth: DRAWER_WIDTH,
  height: '100%',
  backgroundColor: 'sys.neutral.background.default',
};

const SIDE_NAV_STYLES = {
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'hidden',
};

interface DrawerBackdropProps
  extends
    PositionProps,
    TopProps,
    LeftProps,
    ZIndexProps,
    WidthProps,
    HeightProps,
    BackgroundColorProps {
  isOpen?: boolean;
}

const DrawerBackdrop = styled.div<DrawerBackdropProps>`
  ${compose(position, top, left, zIndex, styledWidth, height, styledBackgroundColor)}

  transition: opacity 0.16s linear;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  pointer-events: ${props => (props.isOpen ? 'auto' : 'none')};
`;

export const drawerBackdropStyles: ComponentProps<typeof DrawerBackdrop> = {
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: BACKDROP_Z_INDEX,
  width: '100%',
  height: '100%',
  backgroundColor: 'overlay.default',
};

const getDynamicStyles = ({
  isOpen,
  width,
  isSideNav,
  backgroundColor,
}: Pick<DrawerProps, 'isOpen' | 'width' | 'isSideNav' | 'backgroundColor'>) => {
  return {
    boxShadow: isOpen ? '-4px 2px 6px 0 rgba(0, 0, 0, 0.15)' : 'none',
    ...(width && { themeWidth: width }),
    ...(isSideNav && SIDE_NAV_STYLES),
    ...(backgroundColor && { backgroundColor }),
  };
};

// eslint-disable-next-line complexity
const Drawer = ({
  id,
  isOpen = false,
  isSideNav = false,
  isLeftOpener = false,
  children,
  ariaLabelledby,
  onBackdropClick,
  width,
  containerRef,
  onKeyDown,
  backgroundColor,
  incrementZIndex = false,
  transitionDisabled = false,
}: DrawerProps) => {
  // We use deferred value to improve rendering performance of the drawer.
  // Drawer animation is performed immediately, but the content appears in no blocking-manner.
  // This solution affects INP (Interaction to Next Pain) metric significantly.
  const isDeferredOpen = useDeferredValue(isOpen);

  const { t } = useLocalization();
  useHiddenBodyOverflow(isOpen);

  if (!isOpen && transitionDisabled) {
    return null;
  }

  return (
    <>
      <LazyFocusLock returnFocus={{ preventScroll: false }} disabled={!isOpen}>
        <DrawerContainer
          id={id}
          isOpen={isOpen}
          isLeftOpener={isLeftOpener || isSideNav}
          aria-hidden={!isOpen}
          role="dialog"
          aria-modal="true"
          data-testid="drawer-container"
          {...drawerContainerStyles}
          {...(isLeftOpener && !isOpen ? { themeWidth: 320 } : {})}
          {...getDynamicStyles({ isOpen, width, isSideNav, backgroundColor })}
          {...(ariaLabelledby && isOpen
            ? { 'aria-labelledby': ariaLabelledby, 'data-autofocus': true }
            : { 'aria-label': t('h24_drawer_aria_label') })}
          {...(containerRef ? { ref: containerRef } : {})}
          {...(onKeyDown ? { onKeyDown } : {})}
          {...(incrementZIndex ? { zIndex: MODAL_Z_INDEX + 1 } : {})}
        >
          {isDeferredOpen ? children : null}
        </DrawerContainer>
      </LazyFocusLock>
      <DrawerBackdrop
        {...drawerBackdropStyles}
        {...(incrementZIndex ? { zIndex: BACKDROP_Z_INDEX + 1 } : {})}
        isOpen={isOpen}
        onClick={onBackdropClick}
        aria-hidden={!isOpen}
        data-testid="drawer-backdrop"
      />
    </>
  );
};

export default Drawer;
